import { defaultStringifySearch, useRouter, useSearch } from '@tanstack/react-router';
import { ExternalLink, RotateCw, TriangleAlert } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  BACKGROUND_JOB_PAGE_SIZE,
  BACKGROUND_JOB_STATES,
  BACKGROUND_JOB_STATE_LABELS,
  BACKGROUND_QUEUE_INFO,
  MODULE_DEFINITIONS,
  describeSchedule,
  jobActionRefusalText,
  type BackgroundJobRow,
  type BackgroundQueueSummary,
  type BackgroundWorkOverview,
  type RetryAllResult,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Pager } from '../../../components/Pager';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { Tabs } from '../../../components/Tabs';
import { ToneChip } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime, formatNumber, formatTime, formatZoneName } from '../../../lib/format';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { QUEUE_DASHBOARD_PATH } from '../admin-sections';
import { useBackgroundJobs, useBackgroundWork, useRetryAll } from '../api/background-work-api';
import { ConfirmBeside } from '../components/ConfirmBeside';
import { JobPane } from '../components/background-work/JobPane';
import {
  defaultQueueOf,
  defaultStateOf,
  healthOf,
  readBackgroundWorkSearch,
  type BackgroundTab,
  type BackgroundWorkSearch,
} from '../components/background-work/background-work-address';
import styles from './BackgroundWorkPage.module.css';

/**
 * ADMIN Background work (Admin redesign batch 2, Option A "Our own screen"; owner Q3): the work the system does on
 * its own, over the BullMQ queues. The eight queues with their counts and health, the chosen queue's jobs (failed
 * first), the chosen job with Retry and Remove, and every scheduled job with its next run. Every action writes an
 * event; there is no Pause. Bull Board stays behind "Technical view ↗" for anything deeper.
 */

const PATH = '/admin/background-work';
const OVERLAY_MEDIA = '(max-width: 1180px)';
/** semantic.css's tablet width: the queue list becomes a choice above the jobs. */
const NARROW_MEDIA = '(max-width: 900px)';
const MODULE_LABELS: ReadonlyMap<string, string> = new Map(MODULE_DEFINITIONS.map((definition) => [definition.key, definition.label]));

export function BackgroundWorkPage(): ReactNode {
  const canView = useCanView('admin_config');
  const canEdit = useCanEdit('admin_config');
  const router = useRouter();
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const search = readBackgroundWorkSearch(raw);
  const isOverlay = useMediaQuery(OVERLAY_MEDIA);
  const isNarrow = useMediaQuery(NARROW_MEDIA);

  const overview = useBackgroundWork(canView);
  const summaries = overview.data?.queues;
  const queue = search.queue ?? defaultQueueOf(summaries);
  const summary = summaries?.find((item) => item.queue === queue);
  const state = search.state ?? defaultStateOf(summary);
  const page = search.page ?? 1;
  const isRedisUp = overview.data?.redis === 'up';
  const jobs = useBackgroundJobs(queue, state, page, canView && isRedisUp && (search.tab ?? 'queues') === 'queues');

  const go = (next: BackgroundWorkSearch): void => router.history.replace(`${PATH}${defaultStringifySearch({ ...next })}`);

  if (!canView) return <ForbiddenState resource="background work" contact="Your admin" />;
  if (overview.isError && !overview.data) {
    return (
      <div className={styles.page}>
        <ServerErrorState requestId={overview.error.requestId} onRetry={() => void overview.refetch()} />
      </div>
    );
  }

  const tab: BackgroundTab = search.tab ?? 'queues';
  const failedTotal = summaries?.reduce((sum, item) => sum + (item.counts?.failed ?? 0), 0);
  const rows = jobs.data?.rows ?? [];
  const pane =
    search.job !== undefined && tab === 'queues' ? (
      <JobPane key={`${queue}:${search.job}`} queue={queue} id={search.job} row={rows.find((row) => row.id === search.job)} mode={isOverlay ? 'overlay' : 'docked'} canEdit={canEdit} onClose={() => go({ ...search, job: undefined })} />
    ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <span className={styles.crumb}>Admin · System</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Background work</h1>
          <span className={styles.grow} />
          {/* Bull Board acts on the queues, so it opens only for someone who may change Admin settings. */}
          {canEdit && (
            <a className={styles.technical} href={QUEUE_DASHBOARD_PATH} target="_blank" rel="noreferrer">
              Technical view <ExternalLink size={14} aria-hidden />
            </a>
          )}
        </div>
        <p className={styles.lead}>
          The work the system does on its own — sending email, checking deadlines, reading CVs. If something here is stuck, emails stop going out or reminders stop firing.
        </p>
      </header>

      {overview.data && overview.data.redis !== 'up' && <RedisBanner overview={overview.data} />}
      {!canEdit && <p className={styles.quiet}>You can look at the queues, but not retry or remove jobs.</p>}

      <Tabs<BackgroundTab>
        label="Background work"
        value={tab}
        onChange={(next) => go({ tab: next === 'queues' ? undefined : next })}
        tabs={[
          { value: 'queues', label: 'Queues', ...(failedTotal ? { after: <span className={styles.badge}>{failedTotal}</span>, afterLabel: `${failedTotal} failed` } : {}) },
          { value: 'scheduled', label: 'Scheduled work', after: <span className={styles.tabCount}>{overview.data?.scheduled.length ?? EMPTY}</span> },
        ]}
      >
        {!overview.data ? (
          <div className={styles.panel}>
            <SkeletonLines count={8} />
          </div>
        ) : tab === 'scheduled' ? (
          <ScheduledTable overview={overview.data} />
        ) : (
          <div className={styles.layout} data-pane={pane !== null && !isOverlay ? 'docked' : undefined}>
            {isNarrow ? (
              <QueueChoice summaries={overview.data.queues} queue={queue} onChoose={(next) => go({ queue: next })} />
            ) : (
              <QueueList overview={overview.data} queue={queue} onChoose={(next) => go({ queue: next })} />
            )}
            <section className={styles.jobs} aria-label={BACKGROUND_QUEUE_INFO[queue].label}>
              <QueueHead overview={overview.data} summary={summary} canEdit={canEdit && isRedisUp} />
              <div className={styles.states} role="group" aria-label="Show jobs that are">
                {BACKGROUND_JOB_STATES.map((option) => (
                  <button key={option} type="button" className={styles.state} aria-pressed={state === option} data-state={option} onClick={() => go({ queue, state: option })}>
                    {BACKGROUND_JOB_STATE_LABELS[option]} <span className={styles.stateCount}>{summary?.counts ? formatNumber(summary.counts[option]) : EMPTY}</span>
                  </button>
                ))}
              </div>
              {!isRedisUp ? (
                <p className={styles.quiet}>The jobs can't be listed until the queue store is back.</p>
              ) : jobs.isError && !jobs.data ? (
                <ServerErrorState requestId={jobs.error.requestId} onRetry={() => void jobs.refetch()} />
              ) : jobs.isPending ? (
                <SkeletonLines count={5} />
              ) : rows.length === 0 ? (
                <EmptyState
                  title={state === 'failed' ? 'Nothing has failed here' : `No ${BACKGROUND_JOB_STATE_LABELS[state].toLowerCase()} jobs`}
                  body={state === 'failed' ? 'Jobs that stop after their last try appear here, so you can retry or remove them.' : 'Jobs appear here as the system queues them.'}
                  action={
                    state === 'failed' ? null : (
                      <Button size="sm" onClick={() => go({ queue, state: 'failed' })}>
                        Show failed jobs
                      </Button>
                    )
                  }
                  isInset
                />
              ) : (
                <>
                  <JobTable rows={rows} openId={search.job} onOpen={(id) => go({ ...search, queue, state, job: id })} />
                  {(jobs.data?.total ?? 0) > BACKGROUND_JOB_PAGE_SIZE && (
                    <Pager
                      page={page}
                      pageCount={Math.ceil((jobs.data?.total ?? 0) / BACKGROUND_JOB_PAGE_SIZE)}
                      pageSize={BACKGROUND_JOB_PAGE_SIZE}
                      rowCount={rows.length}
                      total={jobs.data?.total ?? 0}
                      onPage={(next) => go({ queue, state, page: next > 1 ? next : undefined })}
                    />
                  )}
                </>
              )}
              {queue === 'email' && state === 'failed' && rows.length > 0 && (
                <p className={styles.quiet}>Failed emails also appear on Email delivery, where you can fix an address before sending again. Retrying here sends them exactly as they were.</p>
              )}
            </section>
            {pane}
          </div>
        )}
      </Tabs>
    </div>
  );
}

function RedisBanner({ overview }: { readonly overview: BackgroundWorkOverview }): ReactNode {
  return (
    <p className={styles.alarm} role="alert">
      <TriangleAlert size={16} aria-hidden />
      <span>
        {overview.redis === 'not_configured'
          ? 'There is no queue store (Redis) on this server, so nothing runs in the background: no emails go out and nothing runs on a timer.'
          : "The queue store (Redis) can't be reached, so emails wait and nothing runs on a timer. It all resumes by itself when the store is back."}{' '}
        Checked {formatTime(overview.checkedAt)}.
      </span>
    </p>
  );
}

function countsLine(summary: BackgroundQueueSummary): string {
  if (!summary.counts) return EMPTY;
  const parts = [`${formatNumber(summary.counts.waiting)} waiting`, `${formatNumber(summary.counts.active)} running`];
  if (summary.counts.failed > 0) parts.unshift(`${formatNumber(summary.counts.failed)} failed`);
  return parts.join(' · ');
}

/** The eight queues with a health dot; arrow keys move between them (06-qol §2). */
function QueueList({ overview, queue, onChoose }: { readonly overview: BackgroundWorkOverview; readonly queue: string; readonly onChoose: (queue: BackgroundQueueSummary['queue']) => void }): ReactNode {
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const at = overview.queues.findIndex((summary) => summary.queue === queue);
    const next = overview.queues[at + (event.key === 'ArrowDown' ? 1 : -1)];
    if (!next) return;
    event.preventDefault();
    onChoose(next.queue);
    document.getElementById(`queue-${next.queue}`)?.focus();
  };
  return (
    <nav className={styles.queues} aria-label="Queues">
      <ul className={styles.queueList} onKeyDown={onKeyDown}>
        {overview.queues.map((summary) => (
          <li key={summary.queue}>
            <button type="button" id={`queue-${summary.queue}`} className={styles.queue} aria-current={summary.queue === queue || undefined} onClick={() => onChoose(summary.queue)}>
              <span className={styles.dot} data-health={healthOf(summary)} aria-hidden />
              <span className={styles.queueText}>
                <span className={styles.queueName}>{BACKGROUND_QUEUE_INFO[summary.queue].label}</span>
                <span className={styles.queueMeta}>{countsLine(summary)}</span>
              </span>
              {(summary.counts?.failed ?? 0) > 0 && <span className={styles.badge}>{summary.counts?.failed}</span>}
            </button>
          </li>
        ))}
      </ul>
      <p className={styles.health}>
        {overview.redis === 'up' ? `Running on ${overview.workers.connected ?? overview.workers.here} ${(overview.workers.connected ?? overview.workers.here) === 1 ? 'worker' : 'workers'} · Redis connected` : 'Redis not connected'} · checked {formatTime(overview.checkedAt)}
      </p>
    </nav>
  );
}

function QueueChoice({ summaries, queue, onChoose }: { readonly summaries: readonly BackgroundQueueSummary[]; readonly queue: string; readonly onChoose: (queue: BackgroundQueueSummary['queue']) => void }): ReactNode {
  return (
    <Select
      label="Queue"
      value={queue}
      options={summaries.map((summary) => ({ value: summary.queue, label: BACKGROUND_QUEUE_INFO[summary.queue].label, description: countsLine(summary) }))}
      onChange={(value) => {
        const chosen = summaries.find((summary) => summary.queue === value);
        if (chosen) onChoose(chosen.queue);
      }}
    />
  );
}

function QueueHead({ overview, summary, canEdit }: { readonly overview: BackgroundWorkOverview; readonly summary: BackgroundQueueSummary | undefined; readonly canEdit: boolean }): ReactNode {
  const retryAll = useRetryAll();
  const showToast = useToasts((state) => state.show);
  const [report, setReport] = useState<RetryAllResult | null>(null);
  if (!summary) return null;
  const info = BACKGROUND_QUEUE_INFO[summary.queue];
  const failed = summary.counts?.failed ?? 0;
  // Review W10: what Retry all would really retry, not every failed job (an invite's email never goes again).
  const retryable = summary.retryable ?? failed;
  return (
    <div className={styles.queueHead}>
      <div className={styles.queueHeadRow}>
        <h2 className={styles.queueTitle}>{info.label}</h2>
        <span className={styles.mono}>{summary.queue}</span>
        {summary.queue === 'email' && <span className={styles.quiet}>Limit: {overview.emailsPerMinute} emails a minute</span>}
        <span className={styles.grow} />
        {canEdit && failed > 0 && retryable === 0 && <span className={styles.quiet}>None of these can be retried from here</span>}
        {canEdit && retryable > 0 && (
          <ConfirmBeside
            label={`Retry all ${retryable.toLocaleString()}`}
            icon={RotateCw}
            isPending={retryAll.isPending}
            title={`Retry all ${retryable.toLocaleString()}?`}
            body={
              summary.queue === 'email'
                ? 'Each email goes again through the same checks as Send again on Email delivery: one that held a sign-in link, or is already on its way again, is left out and listed. Emails that go can’t be taken back.'
                : `Every failed job here gets a fresh ${overview.attemptsPerJob} tries. Any whose part of the app is switched off is left out and listed.`
            }
            confirmLabel="Retry them"
            onConfirm={() =>
              retryAll.mutate(summary.queue, {
                onSuccess: (result) => {
                  setReport(result);
                  showToast({ tone: result.retried > 0 ? 'success' : 'info', message: `${result.retried.toLocaleString()} retried` });
                },
                onError: (error) => showToast({ tone: 'danger', message: 'Nothing was retried', detail: errorMessage(error) }),
              })
            }
          />
        )}
      </div>
      <p className={styles.quiet}>{info.hint}</p>
      {report && (report.skipped.length > 0 || report.remaining > 0) && (
        <div className={styles.report}>
          <div className={styles.queueHeadRow}>
            <b>
              {report.retried.toLocaleString()} retried, {report.skipped.length.toLocaleString()} left out
            </b>
            <span className={styles.grow} />
            <Button variant="ghost" size="sm" onClick={() => setReport(null)}>
              Close
            </Button>
          </div>
          {report.remaining > 0 && <p className={styles.quiet}>{report.remaining.toLocaleString()} more still failed. Click Retry all once more for the rest.</p>}
          <ul className={styles.skipped}>
            {report.skipped.map((row) => (
              <li key={row.jobId}>
                <b>
                  {row.label} #{row.jobId}
                </b>{' '}
                — {jobActionRefusalText(row.refusal, row.templateKey)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Job · Why it stopped · Last try. Rows are buttons; arrow keys walk them. */
function JobTable({ rows, openId, onOpen }: { readonly rows: readonly BackgroundJobRow[]; readonly openId: string | undefined; readonly onOpen: (id: string) => void }): ReactNode {
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const at = rows.findIndex((row) => row.id === openId);
    const next = rows[at + (event.key === 'ArrowDown' ? 1 : -1)] ?? (at < 0 ? rows[0] : undefined);
    if (!next) return;
    event.preventDefault();
    onOpen(next.id);
    document.getElementById(`job-${next.id}`)?.focus();
  };
  return (
    <ul className={styles.jobList} aria-label="Jobs" onKeyDown={onKeyDown}>
      {rows.map((row) => (
        <li key={row.id}>
          <button type="button" id={`job-${row.id}`} className={styles.job} data-state={row.state} aria-current={row.id === openId || undefined} onClick={() => onOpen(row.id)}>
            <span className={styles.jobName}>
              {row.message ? `${row.message.subject} — to ${row.message.toAddress}` : row.label}
              <span className={styles.mono}> {row.name}</span>
            </span>
            <span className={styles.jobWhy}>{row.state === 'failed' ? (row.failedReason ?? EMPTY) : row.state === 'delayed' && row.runsAt ? `Due ${formatDateTime(row.runsAt)}` : EMPTY}</span>
            <span className={styles.jobWhen}>
              {formatDateTime(row.finishedAt ?? row.startedAt ?? row.addedAt)}
              <span className={styles.quiet}>
                {' '}
                {row.attemptsMade} of {row.maxAttempts} tries
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Every scheduled job, in words, with its next run; "off" while a part it works for is switched off (§L28). */
function ScheduledTable({ overview }: { readonly overview: BackgroundWorkOverview }): ReactNode {
  return (
    <div className={styles.panel}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">What</th>
            <th scope="col">When</th>
            <th scope="col">Next run</th>
            <th scope="col">Queue</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {overview.scheduled.map((row) => {
            const isOff = row.offBecause.length > 0;
            const offParts = row.offBecause.map((key) => MODULE_LABELS.get(key) ?? key);
            return (
              <tr key={row.schedulerId} data-off={isOff || undefined}>
                <td>
                  <span className={styles.jobName}>{row.label}</span>
                  <span className={styles.mono}> {row.jobName}</span>
                </td>
                <td>{describeSchedule(row.pattern, formatZoneName(new Date(), { timeZone: row.timezone }))}</td>
                <td className={styles.number}>{isOff ? EMPTY : row.nextRunAt ? formatDateTime(row.nextRunAt) : EMPTY}</td>
                <td>{BACKGROUND_QUEUE_INFO[row.queue].label}</td>
                <td>
                  {isOff ? (
                    <span>
                      <ToneChip tone="ended">Off</ToneChip>{' '}
                      <span className={styles.quiet}>
                        {offParts.join(' and ')} {offParts.length === 1 ? 'is' : 'are'} switched off
                      </span>
                    </span>
                  ) : (
                    <ToneChip tone="done">On</ToneChip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
